// src/pages/EcxCallback.tsx
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signInWithCustomToken } from "firebase/auth";

type ApiTokenResponse = {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;

  tenantId?: string;

  accountId?: string;
  shopId?: string;
  shopUrl?: string;
  role?: string;
  status?: string;

  firebase_custom_token?: string;
  needsLink?: boolean;

  error?: string;
  message?: string;
  redirectPath?: string; // 409系で返ることがある
  redirectTargets?: { tenantId: string; redirectPath: string }[];
  registeredTenants?: string[];
  [k: string]: any;
};

// -------------------------
// Utils: JWT payload decode (base64url)
// -------------------------
function base64UrlDecodeToString(input: string) {
  const pad = "=".repeat((4 - (input.length % 4)) % 4);
  const b64 = (input + pad).replace(/-/g, "+").replace(/_/g, "/");
  // atob expects latin1; JWT payload is JSON ascii/utf8 (safe in practice)
  return atob(b64);
}

function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = base64UrlDecodeToString(parts[1]);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function cleanup() {
  sessionStorage.removeItem("ecx_oauth_state");
  sessionStorage.removeItem("ecx_code_verifier");
  sessionStorage.removeItem("login_tenant");
  sessionStorage.removeItem("ecx_shop_url");

  // Login側で追加したやつ
  sessionStorage.removeItem("ecx_expected_workspace_public_id");
  sessionStorage.removeItem("ecx_client_id");
  sessionStorage.removeItem("ecx_redirect_uri");

  // sessionStorage喪失対策で退避した一時値を掃除
  localStorage.removeItem("ecx_oauth_state_tmp");
  localStorage.removeItem("ecx_code_verifier_tmp");
}

// ログイン画面に表示するエラー文言（ECX側の設定起因のものはユーザー向けに案内する）
const ECX_LOGIN_ERROR_KEY = "ecx_login_error";
const WORKSPACE_GUIDE =
  "ECX でログインしたワークスペースに、この店舗（shop_url）が紐づいていない可能性があります。" +
  "ECX 管理画面でワークスペースと店舗、およびご自身のアカウントの紐づけをご確認のうえ、正しいワークスペースを選んで再度ログインしてください。";

function loginErrorMessage(code: string | undefined | null, meta?: any): string {
  switch (code) {
    case "workspace_public_id_missing":
      return "ECX からワークスペース情報を取得できませんでした。" + WORKSPACE_GUIDE;
    case "tenant_mismatch":
      return "この店舗は別のテナントに登録されています。" + WORKSPACE_GUIDE;
    case "shop_registered_in_other_tenants": {
      const t = Array.isArray(meta?.registeredTenants) ? meta.registeredTenants.join(", ") : "";
      return (
        `この店舗は別のテナント${t ? `（${t}）` : ""}に登録済みのため、ECX ワークスペースからはログインできません。` +
        WORKSPACE_GUIDE
      );
    }
    case "shop_url_missing":
    case "ecx_user_unlinked":
      return "ECX のワークスペースに店舗が紐づいていません。" + WORKSPACE_GUIDE;
    default:
      return "ECX ログインに失敗しました。" + WORKSPACE_GUIDE;
  }
}

function setLoginError(code: string | undefined | null, meta?: any) {
  try {
    sessionStorage.setItem(ECX_LOGIN_ERROR_KEY, loginErrorMessage(code, meta));
  } catch {}
}

function hardLogoutLocal() {
  // 事故るとややこしいので、ECX関係のlocalStorageを一旦掃除
  localStorage.removeItem("ecx_access_token");
  localStorage.removeItem("ecx_token_expiry");
  localStorage.removeItem("ecx_workspace_public_id");
  // 任意：プロバイダ表記
  localStorage.removeItem("auth_provider");
}

export default function EcxCallback() {
  const navigate = useNavigate();
  const once = useRef(false);

  useEffect(() => {
    if (once.current) return; // ✅ StrictMode二重実行ガード
    once.current = true;

    const run = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const oauthError = urlParams.get("error");
      const oauthErrorDescription = urlParams.get("error_description");

      // /ecx/callback は常に ecx テナントとして扱う
      const tenant = "ecx";
      const loginPath = "/ecx/login";
      sessionStorage.setItem("login_tenant", "ecx");
      localStorage.setItem("selectedTenant", "ecx");

      const savedState =
        sessionStorage.getItem("ecx_oauth_state") || localStorage.getItem("ecx_oauth_state_tmp");
      const codeVerifier =
        sessionStorage.getItem("ecx_code_verifier") || localStorage.getItem("ecx_code_verifier_tmp");

      if (oauthError) {
        console.error("OAuth authorization error:", oauthError, oauthErrorDescription);
        cleanup();
        navigate(loginPath, { replace: true });
        return;
      }

      if (!state || !savedState || state !== savedState) {
        console.error("state mismatch", { state, savedState });
        cleanup();
        navigate(loginPath, { replace: true });
        return;
      }

      if (!code) {
        console.error("missing code");
        cleanup();
        navigate(loginPath, { replace: true });
        return;
      }

      if (!codeVerifier) {
        console.error("missing code_verifier");
        cleanup();
        navigate(loginPath, { replace: true });
        return;
      }

      try {
        // redirect_uri は authorize と完全一致必須
        const redirectUri =
          sessionStorage.getItem("ecx_redirect_uri") || `${window.location.origin}/ecx/callback`;

        // ★ serverがECX_CLIENT_ID未設定でも回せるように client_id を同梱（最小の保険）
        const clientId = sessionStorage.getItem("ecx_client_id") || undefined;

        const resp = await fetch(`/api/auth/ecx/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": "ecx",
          },
          body: JSON.stringify({
            grant_type: "authorization_code",
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
            tenantId: tenant,
            client_id: clientId,
          }),
          cache: "no-store",
        });

        let tokenRes: ApiTokenResponse = {};
        try {
          tokenRes = await resp.json();
        } catch (parseErr) {
          console.error("ECX token response parse error:", parseErr);
          throw new Error(`Token response parse failed: ${String(parseErr)}`);
        }

        // ---- 409: 初回リンク必要など ----
        if (resp.status === 409) {
          console.warn("ECX token exchange returned 409:", tokenRes);

          // 初回リンク（shopUrl未紐付け）の場合は token を保存してリンク画面へ
          if (tokenRes.error === "ecx_user_unlinked" || tokenRes.needsLink) {
            if (tokenRes.access_token) localStorage.setItem("ecx_access_token", tokenRes.access_token);
            if (tokenRes.expires_in) {
              const expiryTime = Date.now() + Number(tokenRes.expires_in) * 1000 - 60 * 1000;
              localStorage.setItem("ecx_token_expiry", String(expiryTime));
            }
            // tenant確定
            localStorage.setItem("selectedTenant", tokenRes.tenantId || tenant);
            localStorage.setItem("auth_provider", "ecx");

            cleanup();
            // あなたのUI設計に合わせて。/check-status がリンクUIならこれでOK
            navigate("/check-status", { replace: true });
            return;
          }

          // ショップが他テナントに登録済み
          if (tokenRes.error === "shop_registered_in_other_tenants") {
            const targets = tokenRes.redirectTargets ?? [];
            const rp = (targets[0]?.redirectPath) || tokenRes.redirectPath || loginPath;
            console.warn("[ECX callback] shop exists in other tenant, redirecting to:", rp, targets);
            setLoginError("shop_registered_in_other_tenants", tokenRes);
            cleanup();
            navigate(rp, { replace: true });
            return;
          }

          // 409でリダイレクト先が返る設計なら尊重
          const rp = tokenRes.redirectPath;
          if (!rp) setLoginError(tokenRes.error, tokenRes);
          cleanup();
          navigate(rp || loginPath, { replace: true });
          return;
        }

        if (!resp.ok) {
          console.error("ECX token exchange failed:", { status: resp.status, body: tokenRes });
          setLoginError(tokenRes?.error, tokenRes);
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        // ---- ★ firebase_custom_token チェック（応答チェック） ----
        if (!tokenRes.firebase_custom_token) {
          console.error("[ECX callback] missing firebase_custom_token in response", {
            status: resp.status,
            keys: Object.keys(tokenRes),
            response: JSON.stringify(tokenRes).slice(0, 500),
          });
          hardLogoutLocal();
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        if (!tokenRes.access_token) {
          console.error("[ECX callback] missing access_token in response", {
            status: resp.status,
            keys: Object.keys(tokenRes),
          });
          hardLogoutLocal();
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        const payload = decodeJwtPayload(tokenRes.access_token);
        const workspacePublicId: string = String(payload?.workspace_public_id ?? "").trim();

        if (!workspacePublicId) {
          console.error("workspace_public_id missing in JWT payload", payload);
          setLoginError("workspace_public_id_missing");
          hardLogoutLocal();
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        // 期待値（優先順：sessionStorageの期待値 -> localStorageの固定値）
        const expectedFromSession = (sessionStorage.getItem("ecx_expected_workspace_public_id") || "").trim();
        const expectedFromLocal = (localStorage.getItem("ecx_workspace_public_id") || "").trim();
        const expected = expectedFromSession || expectedFromLocal;

        if (expected && workspacePublicId !== expected) {
          console.warn("[ECX callback] workspace_public_id mismatch; updating pinned workspace", {
            expected,
            got: workspacePublicId,
            reason: "User may have multiple workspaces or workspace was changed in ECX",
          });

          // 旧ワークスペース固定値が残っていてもログインを止めない。
          // ここで最新のworkspace_public_idに更新して処理続行する。
          localStorage.removeItem("accountId");
          localStorage.removeItem("companyId");
          localStorage.removeItem("selectedCompanyId");
          localStorage.removeItem("shopId");
          localStorage.removeItem("shopUrl");
          localStorage.removeItem("selectedShopUrl");
        }

        // 初回（期待値なし）なら固定する
        if (!expected) {
          localStorage.setItem("ecx_workspace_public_id", workspacePublicId);
        } else {
          // 念のため一致したものも保存しておく（後続で使える）
          localStorage.setItem("ecx_workspace_public_id", workspacePublicId);
        }

        // ---- Firebaseカスタムトークン必須 ----
        if (!tokenRes.firebase_custom_token) {
          console.error("missing firebase_custom_token from server response", tokenRes);
          hardLogoutLocal();
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        // ✅ Firebaseログイン（AuthGate通す）
        try {
          await signInWithCustomToken(getAuth(), tokenRes.firebase_custom_token);
        } catch (firebaseErr: any) {
          console.error("[ECX callback] signInWithCustomToken failed:", {
            code: firebaseErr?.code,
            message: firebaseErr?.message,
            tokenHead: tokenRes.firebase_custom_token?.slice(0, 20),
          });
          hardLogoutLocal();
          cleanup();
          navigate(loginPath, { replace: true });
          return;
        }

        // ✅ localStorage
        localStorage.setItem("selectedTenant", tokenRes.tenantId || tenant);
        localStorage.setItem("auth_provider", "ecx");

        // ECX JWTも保存（linkにも使える）
        localStorage.setItem("ecx_access_token", tokenRes.access_token);

        // ✅ CompanyGateが見る companyId を埋める（accountId→companyId）
        if (tokenRes.accountId) {
          localStorage.setItem("accountId", tokenRes.accountId);
          localStorage.setItem("companyId", tokenRes.accountId); // ★互換
          localStorage.setItem("selectedCompanyId", tokenRes.accountId); // ★互換
        }

        if (tokenRes.shopId) localStorage.setItem("shopId", tokenRes.shopId);
        if (tokenRes.shopUrl) localStorage.setItem("shopUrl", tokenRes.shopUrl);

        if (tokenRes.plan) localStorage.setItem("plan", tokenRes.plan);
        else localStorage.removeItem("plan");

        if (tokenRes.trialStatus) localStorage.setItem("trialStatus", tokenRes.trialStatus);
        else localStorage.removeItem("trialStatus");

        if (tokenRes.expires_in) {
          const expiryTime = Date.now() + Number(tokenRes.expires_in) * 1000 - 60 * 1000;
          localStorage.setItem("ecx_token_expiry", String(expiryTime));
        } else {
          localStorage.removeItem("ecx_token_expiry");
        }

        cleanup();

        // ✅ 新しいセッション開始：古いsessionStorage状態をクリア
        sessionStorage.clear();

        const redirect = localStorage.getItem("postLoginRedirect");
        if (redirect && redirect.startsWith("/super")) {
          localStorage.removeItem("postLoginRedirect");
          window.location.href = redirect;
          return;
        }

        // 既存フロー：/check-status へ
        navigate("/check-status", { replace: true });
      } catch (e) {
        console.error("ECX callback error:", e);
        hardLogoutLocal();
        cleanup();
        navigate("/ecx/login", { replace: true });
      }
    };

    void run();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-sm text-slate-600">サインイン中...</div>
    </div>
  );
}
